import { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, Download, FileSpreadsheet, MessageCircle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { openWhatsApp } from "../utils/whatsappLink";

const TX_TYPE_LABELS = {
  venta: "Ventas",
  premio: "Premios",
  sinpe: "SINPE Móvil",
  banco_tucan: "Banco Tucán",
  banco_payser: "Banco Payser",
  banco_western: "Western Union",
  banco_rapibac: "Rapibac",
  banco_otro: "Otro Banco",
  pago_banco: "Pago desde Banco/Bóveda",
  entrada: "Entradas de Dinero",
  salida: "Salidas de Dinero",
  entrada_banco: "Entrada desde Banco/Bóveda",
  salida_banco: "Salida a Banco/Bóveda",
  pagos_del_dia: "Pagos del Día",
  pago_dias_anteriores_afecta: "Pago Días Anteriores (Afecta)",
  pago_dias_anteriores_no_afecta: "Pago Días Anteriores (NO Afecta)",
  pago_con_premio_afecta: "Pago con Premio (Afecta)",
  cuenta_por_cobrar: "Cuentas por Cobrar",
  cuenta_por_cobrar_cancelada: "Cuentas por Cobrar Canceladas Hoy",
  cuenta_por_pagar: "Cuentas por Pagar",
  cuenta_por_pagar_cancelada: "Cuentas por Pagar Canceladas Hoy",
  aumento_cuenta_por_cobrar: "Aumento Cuenta por Cobrar",
  aumento_cuenta_por_cobrar_cancelada: "Aumento Cuenta por Cobrar Cancelado Hoy",
  pago_cuenta_por_pagar_dias_anteriores: "Pago Cuenta por Pagar (Días Anteriores)",
  pago_cuenta_por_pagar_hoy: "Pago Cuenta por Pagar (Hoy)",
  no_afecta_caja: "No Afecta Caja",
};

const fmtCRC = (n) => `₡${(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSD = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedCRC = (n) => `${n > 0 ? "+" : n < 0 ? "-" : ""}₡${Math.abs(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedUSD = (n) => `${n > 0 ? "+" : n < 0 ? "-" : ""}$${Math.abs(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch {
    return "";
  }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-CR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return iso;
  }
};

const diffColor = (c, d) => {
  if (Math.abs(c) < 0.01 && Math.abs(d) < 0.01) return { bg: "bg-blue-100", text: "text-blue-800", label: "CUADRADO" };
  if (c < 0 || d < 0) return { bg: "bg-red-100", text: "text-red-800", label: "FALTANTE" };
  return { bg: "bg-green-100", text: "text-green-800", label: "SOBRANTE" };
};

export default function DailyReportViewer({ open, onClose, date, boxId, detailLevel }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !date) return;
    const fetch = async () => {
      setLoading(true);
      setData(null);
      try {
        const params = new URLSearchParams({ date, detail_level: detailLevel || "detailed" });
        if (boxId && boxId !== "all") params.append("box_id", boxId);
        const res = await axiosInstance.get(`/reports/daily-movements-data?${params.toString()}`);
        setData(res.data);
      } catch (e) {
        toast.error(getErrorMessage(e, "Error al cargar reporte"));
        onClose?.();
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [open, date, boxId, detailLevel, onClose]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async (kind) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ date, detail_level: detailLevel || "detailed" });
      if (boxId && boxId !== "all") params.append("box_id", boxId);
      const path = kind === "pdf" ? "daily-movements-pdf" : "daily-movements-excel";
      const res = await axiosInstance.get(`/reports/${path}?${params.toString()}`);
      const b64 = kind === "pdf" ? res.data.pdf_base64 : res.data.excel_base64;
      const mime = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const link = document.createElement("a");
      link.href = `data:${mime};base64,${b64}`;
      link.download = res.data.filename;
      link.click();
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(getErrorMessage(e, `Error al descargar ${kind}`));
    } finally {
      setDownloading(false);
    }
  };

  const handleShareWhatsapp = () => {
    if (!data) return;
    const lines = [];
    lines.push(`*Reporte Diario - ${data.box_name}*`);
    lines.push(`Fecha: ${data.date_formatted}`);
    lines.push(`Movimientos: ${data.total_movements}`);
    lines.push("");

    // Apertura de Caja: efectivo con el que se abrió la caja ese día.
    // El backend devuelve un entry por cada turno cerrado (ordenados por
    // closed_at ascendente). Para "Apertura del día" agrupamos por box y
    // tomamos solo el PRIMER turno de cada caja — los siguientes turnos
    // heredan el efectivo del cierre anterior y sumarlos contaría dinero
    // repetido.
    const cdForOpening = data.register_info?.closing_differences || [];
    if (cdForOpening.length) {
      const firstByBox = {};
      cdForOpening.forEach((c) => {
        const key = c.box_id || c.box_name || "default";
        if (!firstByBox[key]) firstByBox[key] = c;
      });
      const openings = Object.values(firstByBox);

      lines.push("*Apertura de Caja:*");
      let totIniC = 0;
      let totIniD = 0;
      openings.forEach((c) => {
        const ic = c.initial_cash_colones || 0;
        const id = c.initial_cash_dollars || 0;
        totIniC += ic;
        totIniD += id;
        const parts = [];
        if (ic > 0 || id === 0) parts.push(fmtCRC(ic));
        if (id > 0) parts.push(fmtUSD(id));
        lines.push(`- ${c.box_name}: ${parts.join(" / ")}`);
      });
      if (openings.length > 1) {
        const tParts = [];
        if (totIniC > 0 || totIniD === 0) tParts.push(fmtCRC(totIniC));
        if (totIniD > 0) tParts.push(fmtUSD(totIniD));
        lines.push(`- *TOTAL:* ${tParts.join(" / ")}`);
      }
      lines.push("");
    }

    const cd = data.register_info?.closing_differences || [];
    if (cd.length) {
      lines.push("*Diferencia de Cierre:*");
      cd.forEach((c) => {
        const lab = c.diferencia_colones < 0 || c.diferencia_dollars < 0 ? "FALTANTE" : c.diferencia_colones > 0 || c.diferencia_dollars > 0 ? "SOBRANTE" : "CUADRADO";
        lines.push(`- ${c.box_name}: ${signedCRC(c.diferencia_colones)} / ${signedUSD(c.diferencia_dollars)} (${lab})`);
      });
      lines.push("");
    }
    // Dynamic totals: include ONLY entries with non-zero colones OR dollars
    const t = data.totals || {};
    const totalsList = [
      ["Ventas Efectivo", t.ventas_colones, t.ventas_dollars],
      ["SINPE Móvil", t.sinpe_colones, t.sinpe_dollars],
      ["Pago por Banco/Tarjeta", t.banco_tarjeta_colones, t.banco_tarjeta_dollars],
      ["Pagos por Banco", t.bancos_colones, t.bancos_dollars],
      ["Pagos desde Banco/Bóveda", t.pago_banco_colones, t.pago_banco_dollars],
      ["Entrada desde Banco/Bóveda", t.entrada_banco_colones, t.entrada_banco_dollars],
      ["Salida a Banco/Bóveda", t.salida_banco_colones, t.salida_banco_dollars],
      ["Entradas de Dinero", t.entradas_colones, t.entradas_dollars],
      ["Premios (Salida)", t.premios_colones, t.premios_dollars],
      ["Salidas de Dinero", t.salidas_colones, t.salidas_dollars],
      ["Pago Días Ant. (Afecta)", t.pago_dias_ant_afecta_colones, t.pago_dias_ant_afecta_dollars],
      ["Cuentas por Cobrar", t.cuentas_cobrar_colones, t.cuentas_cobrar_dollars],
    ];
    const nonZero = totalsList.filter(([, c, d]) => (Math.abs(c || 0) >= 0.01) || (Math.abs(d || 0) >= 0.01));
    if (nonZero.length) {
      lines.push("*Totales:*");
      nonZero.forEach(([label, c, d]) => {
        // Show only the currency side that has data; if both, show both
        const hasC = Math.abs(c || 0) >= 0.01;
        const hasD = Math.abs(d || 0) >= 0.01;
        if (hasC && hasD) lines.push(`- ${label}: ${fmtCRC(c)} / ${fmtUSD(d)}`);
        else if (hasC) lines.push(`- ${label}: ${fmtCRC(c)}`);
        else if (hasD) lines.push(`- ${label}: ${fmtUSD(d)}`);
      });
      lines.push("");
    }
    // Inventory sales total (if any)
    const invSales = data.register_info?.inventory_sales || [];
    const invTotal = invSales.filter((s) => s.status !== "voided").reduce((a, s) => a + (s.total || 0), 0);
    if (invTotal > 0) {
      lines.push(`*Ventas Inventario:* ${fmtCRC(invTotal)} (${invSales.filter((s) => s.status !== "voided").length} ventas)`);
    }
    // Layaway payments
    const layPays = data.register_info?.layaway_payments_today || [];
    const layTotal = layPays.reduce((a, p) => a + (p.amount || 0), 0);
    if (layTotal > 0) {
      lines.push(`*Abonos Apartados:* ${fmtCRC(layTotal)} (${layPays.length} abonos)`);
    }
    // Helper: DD/MM/YYYY from "YYYY-MM-DD"
    const fmtDebtDate = (iso) => {
      if (!iso || iso.length < 10) return "";
      const [y, m, dd] = iso.slice(0, 10).split("-");
      return `${dd}/${m}/${y}`;
    };

    // Cuentas por Cobrar (solo clientes con saldo ACTUAL pendiente)
    const porCobrar = data.por_cobrar_balances || {};
    const porCobrarCustomers = Object.keys(porCobrar).filter((name) => {
      const b = porCobrar[name] || {};
      return (b.current_colones || 0) > 0.01 || (b.current_dollars || 0) > 0.01;
    }).sort();
    if (porCobrarCustomers.length) {
      lines.push("");
      lines.push("*Cuentas por Cobrar:*");
      porCobrarCustomers.forEach((name) => {
        const b = porCobrar[name];
        const curr = b.current_colones || 0;
        const currD = b.current_dollars || 0;
        const parts = [];
        if (curr > 0) parts.push(fmtCRC(curr));
        if (currD > 0) parts.push(fmtUSD(currD));
        const lastDate = fmtDebtDate(b.last_debt_date);
        const dateSuffix = lastDate ? ` (últ. ${lastDate})` : "";
        lines.push(`- ${name}: ${parts.join(" / ")}${dateSuffix}`);
      });
    }
    // Cuentas por Pagar (solo clientes con saldo ACTUAL pendiente)
    const porPagar = data.por_pagar_balances || {};
    const porPagarCustomers = Object.keys(porPagar).filter((name) => {
      const b = porPagar[name] || {};
      return (b.current_colones || 0) > 0.01 || (b.current_dollars || 0) > 0.01;
    }).sort();
    if (porPagarCustomers.length) {
      lines.push("");
      lines.push("*Cuentas por Pagar:*");
      porPagarCustomers.forEach((name) => {
        const b = porPagar[name];
        const curr = b.current_colones || 0;
        const currD = b.current_dollars || 0;
        const parts = [];
        if (curr > 0) parts.push(fmtCRC(curr));
        if (currD > 0) parts.push(fmtUSD(currD));
        const lastDate = fmtDebtDate(b.last_debt_date);
        const dateSuffix = lastDate ? ` (últ. ${lastDate})` : "";
        lines.push(`- ${name}: ${parts.join(" / ")}${dateSuffix}`);
      });
    }
    // Efectivo en Caja (final por caja — viene de closing_differences)
    const closingDiffs = data.register_info?.closing_differences || [];
    if (closingDiffs.length) {
      lines.push("");
      lines.push("*Efectivo en Caja:*");
      let totalFinalC = 0;
      let totalFinalD = 0;
      closingDiffs.forEach((c) => {
        const fc = c.contado_colones || 0;
        const fd = c.contado_dollars || 0;
        totalFinalC += fc;
        totalFinalD += fd;
        const parts = [];
        if (fc > 0 || fd === 0) parts.push(fmtCRC(fc));
        if (fd > 0) parts.push(fmtUSD(fd));
        lines.push(`- ${c.box_name}: ${parts.join(" / ")}`);
      });
      // Total only if more than 1 register
      if (closingDiffs.length > 1) {
        const totalParts = [];
        if (totalFinalC > 0 || totalFinalD === 0) totalParts.push(fmtCRC(totalFinalC));
        if (totalFinalD > 0) totalParts.push(fmtUSD(totalFinalD));
        lines.push(`- *TOTAL:* ${totalParts.join(" / ")}`);
      }

      // *Resumen del día*: Apertura → Cierre → Neto.
      // Apertura = primera del día por cada caja (ya calculada arriba).
      // Cierre = ÚLTIMO turno cerrado por cada caja (último por orden de
      // closed_at, que es como vienen ordenados desde el backend).
      const firstByBoxR = {};
      const lastByBoxR = {};
      closingDiffs.forEach((c) => {
        const key = c.box_id || c.box_name || "default";
        if (!firstByBoxR[key]) firstByBoxR[key] = c;
        lastByBoxR[key] = c;
      });
      let totIniC = 0;
      let totIniD = 0;
      Object.values(firstByBoxR).forEach((c) => {
        totIniC += c.initial_cash_colones || 0;
        totIniD += c.initial_cash_dollars || 0;
      });
      let totFinC = 0;
      let totFinD = 0;
      Object.values(lastByBoxR).forEach((c) => {
        totFinC += c.contado_colones || 0;
        totFinD += c.contado_dollars || 0;
      });
      const netoC = totFinC - totIniC;
      const netoD = totFinD - totIniD;
      const aperturaParts = [];
      if (totIniC > 0 || totIniD === 0) aperturaParts.push(fmtCRC(totIniC));
      if (totIniD > 0) aperturaParts.push(fmtUSD(totIniD));
      const cierreParts = [];
      if (totFinC > 0 || totFinD === 0) cierreParts.push(fmtCRC(totFinC));
      if (totFinD > 0) cierreParts.push(fmtUSD(totFinD));
      const netoParts = [];
      if (totIniC !== 0 || totFinC !== 0) netoParts.push(signedCRC(netoC));
      if (totIniD !== 0 || totFinD !== 0) netoParts.push(signedUSD(netoD));

      lines.push("");
      lines.push("*Resumen del día:*");
      lines.push(`Apertura: ${aperturaParts.join(" / ")} → Cierre: ${cierreParts.join(" / ")}`);
      lines.push(`Neto: ${netoParts.join(" / ")}`);
    }
    const text = lines.join("\n");
    openWhatsApp("", text);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-6xl max-h-[95vh] overflow-y-auto p-0 daily-report-viewer-dialog"
        data-testid="daily-report-viewer-dialog"
      >
        {/* Sticky toolbar (hidden on print) */}
        <div className="sticky top-0 z-20 flex items-center justify-between bg-white border-b border-slate-200 px-6 py-3 print:hidden">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              Reporte Diario {detailLevel === "summary" ? "(Resumido)" : "(Detallado)"}
            </h2>
            {data && (
              <p className="text-xs text-slate-500">
                {data.box_name} — {data.date_formatted} — {data.total_movements} movimientos
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrint}
              disabled={!data || loading}
              data-testid="report-viewer-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDownload("pdf")}
              disabled={!data || loading || downloading}
              data-testid="report-viewer-download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDownload("excel")}
              disabled={!data || loading || downloading}
              data-testid="report-viewer-download-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleShareWhatsapp}
              disabled={!data || loading}
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
              data-testid="report-viewer-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="report-viewer-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6 print:px-4 print:py-2 daily-report-print-area">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
              <p className="text-slate-500">Cargando reporte...</p>
            </div>
          )}

          {!loading && data && <ReportContent data={data} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportContent({ data }) {
  const ri = data.register_info || {};
  const cd = ri.closing_differences || [];
  const t = data.totals || {};
  const usesDollars = ri.uses_dollars !== false;
  const isSummary = data.detail_level === "summary";

  // Group transactions by type for detail mode
  // If a debt-related transaction (cuenta_por_cobrar / cuenta_por_pagar / aumento_cuenta_por_cobrar)
  // links to a debt that is already paid, route it to a *_cancelada virtual group
  // so users see it under "Canceladas Hoy" instead of mixed with pending ones.
  const debtMap = data.debt_map || {};
  const grouped = {};
  (data.transactions || []).forEach((tx) => {
    let ty = tx.type || "otros";
    if (
      ["cuenta_por_cobrar", "cuenta_por_pagar", "aumento_cuenta_por_cobrar"].includes(ty) &&
      tx.debt_id &&
      debtMap[tx.debt_id] &&
      debtMap[tx.debt_id].status === "pagada"
    ) {
      ty = `${ty}_cancelada`;
    }
    if (!grouped[ty]) grouped[ty] = [];
    grouped[ty].push(tx);
  });

  return (
    <div className="space-y-6 text-slate-800">
      {/* Title block */}
      <div className="text-center border-b-2 border-blue-700 pb-4">
        <h1 className="text-2xl font-bold text-blue-900" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          PaliStore — Movimientos del Día
        </h1>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-600">
          <div><span className="font-semibold">Fecha:</span> {data.date_formatted}</div>
          <div><span className="font-semibold">Caja:</span> {data.box_name}</div>
          <div><span className="font-semibold">Movimientos:</span> {data.total_movements}</div>
          <div><span className="font-semibold">Tipo:</span> {isSummary ? "Resumen" : "Detallado"}</div>
        </div>
        {(ri.opened_at || ri.closed_at) && (
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div><span className="font-semibold">Apertura:</span> {fmtDateTime(ri.opened_at)}</div>
            <div><span className="font-semibold">Cierre:</span> {fmtDateTime(ri.closed_at)}</div>
          </div>
        )}
      </div>

      {/* Resumen de Totales */}
      <Section title="Resumen de Totales" color="blue">
        <ResumenTable t={t} usesDollars={usesDollars} />
      </Section>

      {/* Desglose por Datáfono (solo si hubo pagos con tarjeta hoy) */}
      {(data.datafono_breakdown || []).length > 0 && (
        <Section title="Desglose de Tarjeta por Datáfono" color="emerald">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-emerald-700 text-white">
                  <th className="p-2 text-left">Datáfono</th>
                  <th className="p-2 text-left">Banco Destino</th>
                  <th className="p-2 text-center">Transacciones</th>
                  <th className="p-2 text-right">Crédito (₡)</th>
                  <th className="p-2 text-right">Débito (₡)</th>
                  <th className="p-2 text-right">Total (₡)</th>
                </tr>
              </thead>
              <tbody>
                {(data.datafono_breakdown || []).map((row, i) => (
                  <tr key={i} className="border-b border-emerald-100 even:bg-emerald-50/30">
                    <td className="p-2 font-semibold">💳 {row.datafono_name}</td>
                    <td className="p-2">🏦 {row.bank_name}</td>
                    <td className="p-2 text-center">{row.count}</td>
                    <td className="p-2 text-right font-mono">{fmtCRC(row.credito_colones)}</td>
                    <td className="p-2 text-right font-mono">{fmtCRC(row.debito_colones)}</td>
                    <td className="p-2 text-right font-mono font-bold text-emerald-800">{fmtCRC(row.total_colones)}</td>
                  </tr>
                ))}
                <tr className="bg-emerald-100 font-bold">
                  <td colSpan={2} className="p-2 text-right">TOTAL:</td>
                  <td className="p-2 text-center">{(data.datafono_breakdown || []).reduce((s, r) => s + (r.count || 0), 0)}</td>
                  <td className="p-2 text-right font-mono">{fmtCRC((data.datafono_breakdown || []).reduce((s, r) => s + (r.credito_colones || 0), 0))}</td>
                  <td className="p-2 text-right font-mono">{fmtCRC((data.datafono_breakdown || []).reduce((s, r) => s + (r.debito_colones || 0), 0))}</td>
                  <td className="p-2 text-right font-mono text-emerald-900">{fmtCRC((data.datafono_breakdown || []).reduce((s, r) => s + (r.total_colones || 0), 0))}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-slate-500 mt-2">Los montos ya se acreditaron automáticamente a los bancos correspondientes.</p>
          </div>
        </Section>
      )}

      {/* Diferencia de Cierre */}
      {cd.length > 0 && (
        <Section title="Diferencia de Cierre" color="red">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-red-700 text-white">
                  <th className="p-2 text-left">Caja</th>
                  <th className="p-2 text-right">Esperado (₡)</th>
                  <th className="p-2 text-right">Contado (₡)</th>
                  <th className="p-2 text-right">Diferencia (₡)</th>
                  {usesDollars && <th className="p-2 text-right">Diferencia ($)</th>}
                  <th className="p-2 text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {cd.map((c, i) => {
                  const cls = diffColor(c.diferencia_colones, c.diferencia_dollars);
                  return (
                    <tr key={i} className="border-b border-red-200">
                      <td className="p-2 font-medium">{c.box_name}</td>
                      <td className="p-2 text-right">{fmtCRC(c.esperado_colones)}</td>
                      <td className="p-2 text-right">{fmtCRC(c.contado_colones)}</td>
                      <td className={`p-2 text-right font-bold ${cls.bg} ${cls.text}`}>{signedCRC(c.diferencia_colones)}</td>
                      {usesDollars && <td className={`p-2 text-right font-bold ${cls.bg} ${cls.text}`}>{signedUSD(c.diferencia_dollars)}</td>}
                      <td className={`p-2 text-center font-bold ${cls.bg} ${cls.text}`}>{cls.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-2 italic">
            Diferencia = Efectivo Contado − Efectivo Esperado. Negativo = Faltante (hizo falta dinero), Positivo = Sobrante (sobró dinero).
          </p>
        </Section>
      )}

      {/* Cálculo Informativo - Saldos Externos */}
      {ri.final_cash_colones != null && (
        <Section title="Cálculo Informativo - Saldos Externos" color="orange">
          <SaldosExternosTable ri={ri} usesDollars={usesDollars} />
          <p className="text-xs text-slate-500 mt-2 italic">* Este cálculo es solo informativo y NO afecta el efectivo de la caja.</p>
        </Section>
      )}

      {/* Ventas de Inventario */}
      {(ri.inventory_sales || []).length > 0 && (
        <Section title="Ventas de Inventario" color="green">
          <InventorySalesTable sales={ri.inventory_sales} />
        </Section>
      )}

      {/* Abonos de Apartados */}
      {(ri.layaway_payments_today || []).length > 0 && (
        <Section title="Abonos de Apartados del Día" color="amber">
          <LayawayPaymentsTable payments={ri.layaway_payments_today} />
        </Section>
      )}

      {/* Apartados Pendientes */}
      {(ri.active_layaways || []).length > 0 && (
        <Section title="Apartados Pendientes" color="red">
          <ActiveLayawaysTable layaways={ri.active_layaways} />
        </Section>
      )}

      {/* Salidas de Inventario (regalía/retiro/daño/otro) */}
      {(ri.inventory_withdrawals || []).length > 0 && (
        <Section
          title={`Salidas de Inventario (regalía/retiro) — Costo total ₡${(ri.inventory_withdrawals_total_cost || 0).toLocaleString()}`}
          color="amber"
        >
          <InventoryWithdrawalsTable withdrawals={ri.inventory_withdrawals} />
        </Section>
      )}

      {/* Movimientos detallados (detailed mode only) */}
      {!isSummary && Object.keys(grouped).length > 0 && (
        <Section title="Movimientos Detallados por Tipo" color="slate">
          {Object.entries(grouped).map(([ty, txs]) => (
            <TransactionGroup key={ty} type={ty} txs={txs} debtMap={data.debt_map || {}} />
          ))}
        </Section>
      )}

      {/* Cuentas por cliente (current balances) */}
      {Object.keys(data.customer_current_balances || {}).length > 0 && (
        <Section title="Saldos Actuales por Cliente" color="indigo">
          <CustomerBalancesTable
            current={data.customer_current_balances}
            previous={data.customer_previous_balances || {}}
          />
        </Section>
      )}
    </div>
  );
}

function Section({ title, color, children }) {
  const colorMap = {
    blue: "border-blue-200 bg-blue-50/40", red: "border-red-200 bg-red-50/40",
    orange: "border-orange-200 bg-orange-50/40", green: "border-green-200 bg-green-50/40",
    amber: "border-amber-200 bg-amber-50/40", slate: "border-slate-200 bg-slate-50/40",
    indigo: "border-indigo-200 bg-indigo-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
  };
  const titleMap = {
    blue: "text-blue-900", red: "text-red-700", orange: "text-orange-700",
    green: "text-green-700", amber: "text-amber-700", slate: "text-slate-700",
    indigo: "text-indigo-700", emerald: "text-emerald-700",
  };
  return (
    <div className={`rounded-lg border-2 ${colorMap[color] || colorMap.slate} p-4 print:break-inside-avoid`}>
      <h3 className={`text-base font-bold mb-3 ${titleMap[color] || titleMap.slate}`}>{title}</h3>
      {children}
    </div>
  );
}

function ResumenTable({ t, usesDollars = true }) {
  const rows = [
    ["Ventas Efectivo", t.ventas_colones, t.ventas_dollars],
    ["SINPE Móvil", t.sinpe_colones, t.sinpe_dollars],
    ["Pago por Banco/Tarjeta", t.banco_tarjeta_colones, t.banco_tarjeta_dollars],
    ["Tarjeta (Datáfonos)", t.tarjeta_datafono_colones, 0],
    ...(t.tarjeta_sin_datafono_colones > 0 ? [["Tarjeta (sin datáfono)", t.tarjeta_sin_datafono_colones, 0]] : []),
    ["Pagos por Banco", t.bancos_colones, t.bancos_dollars],
    ["Pagos desde Banco/Bóveda", t.pago_banco_colones, t.pago_banco_dollars],
    ["Entrada desde Banco/Bóveda", t.entrada_banco_colones, t.entrada_banco_dollars],
    ["Salida a Banco/Bóveda", t.salida_banco_colones, t.salida_banco_dollars],
    ["Entradas de Dinero", t.entradas_colones, t.entradas_dollars],
    ["Premios (Salida)", t.premios_colones, t.premios_dollars],
    ["Salidas de Dinero", t.salidas_colones, t.salidas_dollars],
    ["Pago Días Anteriores (Afecta)", t.pago_dias_ant_afecta_colones, t.pago_dias_ant_afecta_dollars],
    ["Cuentas por Cobrar", t.cuentas_cobrar_colones, t.cuentas_cobrar_dollars],
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-blue-900 text-white">
            <th className="p-2 text-left">Concepto</th>
            <th className="p-2 text-right">Colones (₡)</th>
            {usesDollars && <th className="p-2 text-right">Dólares ($)</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, c, d], i) => (
            <tr key={i} className="border-b border-blue-100 even:bg-blue-50/30">
              <td className="p-2">{label}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(c)}</td>
              {usesDollars && <td className="p-2 text-right font-mono">{fmtUSD(d)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SaldosExternosTable({ ri, usesDollars = true }) {
  const efe_c = ri.final_cash_colones || 0;
  const efe_d = ri.final_cash_dollars || 0;
  const cob_c = ri.total_cuentas_por_cobrar_colones || 0;
  const cob_d = ri.total_cuentas_por_cobrar_dollars || 0;
  const pag_c = ri.total_cuentas_por_pagar_colones || 0;
  const pag_d = ri.total_cuentas_por_pagar_dollars || 0;
  const com_c = ri.commission_colones || 0;
  const com_d = ri.commission_dollars || 0;
  const api_c = ri.saldo_api_colones || 0;
  const api_d = ri.saldo_api_dollars || 0;
  const tiro_c = ri.saldo_tiro_colones || 0;
  const tiro_d = ri.saldo_tiro_dollars || 0;
  const sub_c = efe_c + cob_c - pag_c - com_c;
  const sub_d = efe_d + cob_d - pag_d - com_d;
  const final_c = sub_c - api_c - tiro_c;
  const final_d = sub_d - api_d - tiro_d;
  const rows = [
    ["Efectivo Actual", efe_c, efe_d, "normal"],
    ["+ Cuentas por Cobrar", cob_c, cob_d, "normal"],
    ["- Cuentas por Pagar", pag_c, pag_d, "normal"],
    ["- Comisiones del Día", com_c, com_d, "normal"],
    ["= Subtotal", sub_c, sub_d, "subtotal"],
    ["- SALDO API", api_c, api_d, "normal"],
    ["- SALDO TIRO", tiro_c, tiro_d, "normal"],
    ["= TOTAL INFORMATIVO", final_c, final_d, "total"],
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-orange-600 text-white">
            <th className="p-2 text-left">Concepto</th>
            <th className="p-2 text-right">Colones (₡)</th>
            {usesDollars && <th className="p-2 text-right">Dólares ($)</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, c, d, kind], i) => (
            <tr key={i} className={
              kind === "total" ? "bg-orange-600 text-white font-bold"
                : kind === "subtotal" ? "bg-orange-100 font-semibold"
                : "border-b border-orange-100"
            }>
              <td className="p-2">{label}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(c)}</td>
              {usesDollars && <td className="p-2 text-right font-mono">{fmtUSD(d)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InventorySalesTable({ sales }) {
  let total = 0;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-green-700 text-white">
            <th className="p-2 text-left">Hora</th>
            <th className="p-2 text-left">Productos</th>
            <th className="p-2 text-left">Método</th>
            <th className="p-2 text-right">Desc.</th>
            <th className="p-2 text-right">Total</th>
            <th className="p-2 text-center">Estado</th>
          </tr>
        </thead>
        <tbody>
          {sales.map((s, i) => {
            const isVoid = s.status === "voided";
            if (!isVoid) total += s.total || 0;
            const prods = (s.products || []).map((p) => `${p.name}(${p.quantity || 1})`).join(", ");
            return (
              <tr key={i} className={`border-b ${isVoid ? "line-through text-slate-400" : ""}`}>
                <td className="p-2">{fmtTime(s.created_at)}</td>
                <td className="p-2 text-xs">{prods}</td>
                <td className="p-2 text-xs">{(s.payment_method || "efectivo").toUpperCase()}</td>
                <td className="p-2 text-right text-xs">{s.discount_percentage > 0 ? `-${s.discount_percentage}%` : ""}</td>
                <td className="p-2 text-right font-mono">{fmtCRC(s.total)}</td>
                <td className="p-2 text-center">{isVoid ? "ANULADA" : "OK"}</td>
              </tr>
            );
          })}
          <tr className="bg-green-100 font-bold">
            <td colSpan={4} className="p-2 text-right">TOTAL:</td>
            <td className="p-2 text-right font-mono">{fmtCRC(total)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LayawayPaymentsTable({ payments }) {
  const total = payments.reduce((a, p) => a + (p.amount || 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-amber-600 text-white">
            <th className="p-2 text-left">Apartado</th>
            <th className="p-2 text-left">Cliente</th>
            <th className="p-2 text-left">Método</th>
            <th className="p-2 text-right">Monto</th>
            <th className="p-2 text-left">Nota</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p, i) => (
            <tr key={i} className="border-b">
              <td className="p-2">{p.layaway_number}</td>
              <td className="p-2">{p.customer_name}</td>
              <td className="p-2 text-xs">{(p.payment_method || "").toUpperCase()}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(p.amount)}</td>
              <td className="p-2 text-xs">{p.notes}</td>
            </tr>
          ))}
          <tr className="bg-amber-100 font-bold">
            <td colSpan={3} className="p-2 text-right">TOTAL:</td>
            <td className="p-2 text-right font-mono">{fmtCRC(total)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ActiveLayawaysTable({ layaways }) {
  const total = layaways.reduce((a, l) => a + (l.remaining || 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-red-600 text-white">
            <th className="p-2 text-left">Número</th>
            <th className="p-2 text-left">Cliente</th>
            <th className="p-2 text-right">Total</th>
            <th className="p-2 text-right">Pagado</th>
            <th className="p-2 text-right">Saldo</th>
            <th className="p-2 text-left">Fecha</th>
          </tr>
        </thead>
        <tbody>
          {layaways.map((l, i) => (
            <tr key={i} className="border-b">
              <td className="p-2">{l.number}</td>
              <td className="p-2">{l.customer_name}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(l.total)}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(l.total_paid)}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(l.remaining)}</td>
              <td className="p-2 text-xs">{(l.created_at || "").substring(0, 10)}</td>
            </tr>
          ))}
          <tr className="bg-red-100 font-bold">
            <td colSpan={4} className="p-2 text-right">TOTAL PENDIENTE:</td>
            <td className="p-2 text-right font-mono">{fmtCRC(total)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const REASON_LABELS = {
  regalia: "🎁 Regalía",
  retiro_propietario: "🏠 Retiro propietario",
  dano: "⚠️ Daño",
  otro: "📝 Otro",
};

function InventoryWithdrawalsTable({ withdrawals }) {
  const totalCost = withdrawals.reduce((a, w) => a + (w.total_cost || 0), 0);
  const totalUnits = withdrawals.reduce((a, w) => a + (w.quantity || 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-amber-600 text-white">
            <th className="p-2 text-left">Hora</th>
            <th className="p-2 text-left">Código</th>
            <th className="p-2 text-left">Producto</th>
            <th className="p-2 text-left">Motivo</th>
            <th className="p-2 text-right">Cant.</th>
            <th className="p-2 text-right">Costo unit.</th>
            <th className="p-2 text-right">Costo total</th>
            <th className="p-2 text-left">Notas</th>
            <th className="p-2 text-left">Por</th>
          </tr>
        </thead>
        <tbody>
          {withdrawals.map((w, i) => (
            <tr key={w.id || i} className="border-b">
              <td className="p-2 text-xs">{(w.created_at || "").substring(11, 16)}</td>
              <td className="p-2 font-mono text-xs">{w.product_code || ""}</td>
              <td className="p-2">{w.product_name || ""}</td>
              <td className="p-2 text-xs">{REASON_LABELS[w.reason] || w.reason || ""}</td>
              <td className="p-2 text-right font-mono">{w.quantity || 0}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(w.unit_cost || 0)}</td>
              <td className="p-2 text-right font-mono">{fmtCRC(w.total_cost || 0)}</td>
              <td className="p-2 text-xs text-slate-600">{w.notes || ""}</td>
              <td className="p-2 text-xs">{w.created_by || ""}</td>
            </tr>
          ))}
          <tr className="bg-amber-100 font-bold">
            <td colSpan={4} className="p-2 text-right">TOTAL:</td>
            <td className="p-2 text-right font-mono">{totalUnits}</td>
            <td></td>
            <td className="p-2 text-right font-mono">{fmtCRC(totalCost)}</td>
            <td colSpan={2}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function TransactionGroup({ type, txs, debtMap }) {
  const label = TX_TYPE_LABELS[type] || type.toUpperCase();
  const totalC = txs.reduce((a, t) => a + (t.amount_colones || 0), 0);
  const totalD = txs.reduce((a, t) => a + (t.amount_dollars || 0), 0);
  return (
    <div className="mb-4">
      <h4 className="text-sm font-bold text-slate-700 bg-slate-200 px-3 py-1.5 rounded-t">
        {label} ({txs.length} movimientos)
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-slate-100 border-b">
              <th className="p-1.5 text-left">Hora</th>
              <th className="p-1.5 text-left">Descripción</th>
              <th className="p-1.5 text-right">Colones</th>
              <th className="p-1.5 text-right">Dólares</th>
              <th className="p-1.5 text-left">Usuario</th>
            </tr>
          </thead>
          <tbody>
            {txs.map((t, i) => {
              const debt = t.debt_id ? debtMap[t.debt_id] : null;
              const desc = t.description || (debt ? debt.customer_name : "");
              return (
                <tr key={i} className={`border-b ${t.voided ? "line-through text-slate-400" : ""}`}>
                  <td className="p-1.5">{fmtTime(t.created_at)}</td>
                  <td className="p-1.5">{desc}</td>
                  <td className="p-1.5 text-right font-mono">{fmtCRC(t.amount_colones)}</td>
                  <td className="p-1.5 text-right font-mono">{fmtUSD(t.amount_dollars)}</td>
                  <td className="p-1.5 text-xs">{t.created_by || ""}</td>
                </tr>
              );
            })}
            <tr className="bg-slate-200 font-bold">
              <td colSpan={2} className="p-1.5 text-right">Total:</td>
              <td className="p-1.5 text-right font-mono">{fmtCRC(totalC)}</td>
              <td className="p-1.5 text-right font-mono">{fmtUSD(totalD)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomerBalancesTable({ current, previous }) {
  // La data trae, por cada entrada, {name, type: 'por_cobrar'|'por_pagar', colones, dollars, last_debt_date}
  const entries = Object.entries(current || {})
    .map(([key, v]) => ({
      name: (v && v.name) || String(key).split('|')[0],
      type: (v && v.type) || 'por_cobrar',
      colones: (v && v.colones) || 0,
      dollars: (v && v.dollars) || 0,
      last_debt_date: (v && v.last_debt_date) || '',
    }))
    .filter((e) => (e.colones || 0) > 0.01 || (e.dollars || 0) > 0.01);

  const cobrar = entries.filter((e) => e.type === 'por_cobrar').sort((a, b) => a.name.localeCompare(b.name));
  const pagar = entries.filter((e) => e.type === 'por_pagar').sort((a, b) => a.name.localeCompare(b.name));

  if (cobrar.length === 0 && pagar.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic py-4 text-center">
        No hay clientes con saldo pendiente.
      </p>
    );
  }

  const sum = (arr, k) => arr.reduce((s, e) => s + (e[k] || 0), 0);
  const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('es-CR') : '—');

  return (
    <div className="space-y-6">
      {/* GRUPO 1: Cuentas por Cobrar (clientes que me deben) */}
      <div data-testid="saldos-cuentas-por-cobrar">
        <h4 className="text-sm font-bold text-indigo-800 mb-2">
          Cuentas por Cobrar <span className="font-normal text-slate-500">— clientes que me deben</span>
        </h4>
        {cobrar.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-2">Sin cuentas por cobrar.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-indigo-700 text-white">
                  <th className="p-2 text-left">Cliente</th>
                  <th className="p-2 text-left">Última Deuda</th>
                  <th className="p-2 text-right">Saldo Pendiente (₡)</th>
                  <th className="p-2 text-right">Saldo Pendiente ($)</th>
                </tr>
              </thead>
              <tbody>
                {cobrar.map((e, i) => (
                  <tr key={i} className="border-b even:bg-indigo-50/30">
                    <td className="p-2">{e.name}</td>
                    <td className="p-2 text-slate-600 text-xs">{fmtDate(e.last_debt_date)}</td>
                    <td className="p-2 text-right font-mono font-bold">{fmtCRC(e.colones)}</td>
                    <td className="p-2 text-right font-mono font-bold">{fmtUSD(e.dollars)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-indigo-100 font-bold text-indigo-900" data-testid="subtotal-por-cobrar">
                  <td className="p-2" colSpan={2}>Subtotal por Cobrar ({cobrar.length})</td>
                  <td className="p-2 text-right font-mono">{fmtCRC(sum(cobrar, 'colones'))}</td>
                  <td className="p-2 text-right font-mono">{fmtUSD(sum(cobrar, 'dollars'))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* GRUPO 2: Saldos a Favor / Cuentas por Pagar (dinero que les debo a ellos) */}
      <div data-testid="saldos-a-favor">
        <h4 className="text-sm font-bold text-emerald-800 mb-2">
          Saldos a Favor / Cuentas por Pagar <span className="font-normal text-slate-500">— dinero que les debo a ellos</span>
        </h4>
        {pagar.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-2">Sin saldos a favor.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-emerald-700 text-white">
                  <th className="p-2 text-left">Cliente</th>
                  <th className="p-2 text-right">Monto a Favor (₡)</th>
                  <th className="p-2 text-right">Monto a Favor ($)</th>
                </tr>
              </thead>
              <tbody>
                {pagar.map((e, i) => (
                  <tr key={i} className="border-b even:bg-emerald-50/30">
                    <td className="p-2">{e.name}</td>
                    <td className="p-2 text-right font-mono font-bold">{fmtCRC(e.colones)}</td>
                    <td className="p-2 text-right font-mono font-bold">{fmtUSD(e.dollars)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-emerald-100 font-bold text-emerald-900" data-testid="subtotal-a-favor">
                  <td className="p-2">Subtotal a Favor ({pagar.length})</td>
                  <td className="p-2 text-right font-mono">{fmtCRC(sum(pagar, 'colones'))}</td>
                  <td className="p-2 text-right font-mono">{fmtUSD(sum(pagar, 'dollars'))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
